import { Platform, StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';

/** Reference width for scaling card dimensions proportionally. */
const BASE_WIDTH = 160

/**
 * Group cards are taller than avatar cards (full-width square cover + name + members/rank below).
 * Exported so the groups-grid "Create" tile (ActionCard) can match their height exactly.
 */
export const GROUP_CARD_ASPECT_RATIO = 0.68

/**
 * Get scaled card dimensions based on card width.
 * Returns proportional sizes for avatar, fonts, padding, etc.
 * When compact is true (e.g. invite modal), uses smaller minimum font sizes so text fits.
 */
export const getCardDimensions = (cardWidth: number, compact?: boolean) => {
  const scale = cardWidth / BASE_WIDTH;
  const minName = compact ? 9 : 14;
  const minStat = compact ? 8 : 10;

  return {
    avatarSize: 96 * scale,
    /** Friend avatar renders larger than the group cover (podium prominence); groups keep avatarSize. */
    friendAvatarSize: 112 * scale,
    /** Group cover is a near-full-width square (design), not the small avatar size. */
    groupImageSize: cardWidth - 32,
    fontSizeName: Math.max(minName, 16 * scale),
    fontSizeStat: Math.max(minStat, 12 * scale),
    paddingTop: 16 * scale,
    paddingBottom: 18 * scale,
    paddingHorizontal: 12 * scale,
    gap: 12 * scale,
    removeButtonSize: 20 * scale,
    removeButtonPadding: 6 * scale,
    statusDotSize: 18 * scale,
    statusDotBorder: 3 * scale,
    /** Profile badge (Gmail/Facebook pic or initials) at bottom-right of avatar. */
    profileBadgeSize: Math.max(22, 28 * scale),
    profileBadgeBorder: 2.5 * scale,
    avatarMarginBottom: 10 * scale,
    nameMarginBottom: 5 * scale,
    stageSplitOffset: 6 * scale,
  };
};

export const getFriendCardBaseStyles = (theme: Theme, themeMode?: ThemeMode) =>
  StyleSheet.create({
    friendCard: {
      backgroundColor: themeMode === 'dark' ? theme.background.primary : theme.background.card,
      // Approved design: radius-xl 16, lavender glow border + soft 18px lavender shadow.
      borderRadius: 16,
      borderWidth: 1.5,
      borderColor: themeMode === 'dark' ? `${theme.primary}4D` : theme.gray[300],
      shadowColor: themeMode === 'dark' ? theme.primary : theme.shadow,
      shadowOffset: {
        width: 0,
        height: themeMode === 'dark' ? 0 : 6,
      },
      shadowOpacity: themeMode === 'dark' ? 0.4 : 0.18,
      shadowRadius: themeMode === 'dark' ? 18 : 12,
      elevation: themeMode === 'dark' ? 11 : 9,
      width: '100%',
      alignSelf: 'flex-start',
      overflow: 'hidden',
      aspectRatio: 0.85,
    },
    friendCardAcceptedOuter: {
      width: '100%',
    },
    // Group cards are taller: full-width square cover + name + members/rank below (design).
    friendCardGroup: {
      aspectRatio: GROUP_CARD_ASPECT_RATIO,
    },
    /**
     * Participant picker Pressable must shrink-wrap content; if it stretches to the row height,
     * the selection border outlines empty space below the card (looks like an extra card section).
     * Same corner radius as `friendCard` so the selection border isn't square while the card is rounded.
     */
    friendCardSelectionHost: {
      width: '100%',
      alignSelf: 'flex-start',
      borderRadius: 16,
      ...(Platform.OS === 'web'
        ? ({
            outlineWidth: 0,
          } as import('react-native').ViewStyle)
        : {}),
    },
    friendCardSelected: {
      borderWidth: 2,
      borderColor: theme.primary,
      ...(themeMode === 'dark'
        ? {
            shadowColor: theme.primary,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.7,
            shadowRadius: 16,
            elevation: 14,
          }
        : {}),
    },
    friendCardUnselected: {
      borderWidth: 0,
      borderColor: 'transparent',
    },
    friendCardPending: {},
    friendTouchable: {
      width: '100%',
      height: '100%',
    },
    cardContent: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingTop: 16,
      paddingBottom: 18,
      paddingHorizontal: 12,
      position: 'relative',
      width: '100%',
    },
    // Avatar (friend) cards: distribute name → avatar-on-podium → footer top-to-bottom
    // like the approved design (no dead space at the bottom). Not used for group image cards.
    cardContentSpread: {
      justifyContent: 'space-between',
    },
    cardStageTop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor:
        themeMode === 'dark'
          ? theme.background.primary
          : themeMode === 'light'
            ? theme.background.primary
            : theme.primary,
    },
    // Approved design = a single flat card surface. The podium (shadow pool + floor bar) now
    // lives under the avatar (see FriendCardAvatar styles); these two legacy stage layers are
    // neutralized so the card is one clean surface instead of the old two-tone split.
    cardStageBottom: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 0,
      backgroundColor: 'transparent',
    },
    stageSplitShadow: {
      width: 0,
      height: 0,
      backgroundColor: 'transparent',
    },
    removeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      padding: 6,
      zIndex: 10,
    },
    removeButtonPressed: { opacity: 0.7 },
    removeButtonHover: { opacity: 0.9 },
    cardBadgeContainer: {
      position: 'absolute',
      top: 8,
      left: 8,
      zIndex: 5,
    },
    selectionIndicator: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: theme.primary,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
    selectionIndicatorText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.white,
    },
  });

export const getCardContentStyle = (dimensions: ReturnType<typeof getCardDimensions>) => ({
  paddingTop: dimensions.paddingTop,
  paddingBottom: dimensions.paddingBottom,
  paddingHorizontal: dimensions.paddingHorizontal,
});

export const getRemoveButtonStyle = (dimensions: ReturnType<typeof getCardDimensions>) => ({
  padding: dimensions.removeButtonPadding,
});

export const getSelectionIndicatorStyle = (_dimensions: ReturnType<typeof getCardDimensions>) => ({
  width: 24,
  height: 24,
  borderRadius: 12,
});
